import { useEffect, useMemo, useCallback, useRef, Dispatch, SetStateAction } from 'react'
import { type WorkerSelectionItem } from './WorkersSelection'
import { type WorkAreaSelection } from './WorkAreaSection'
import { type UploadedFileItem } from './RequiredDocumentsSection'
import {
  type PermitType,
  type TemplateConfiguration,
  type TemplateField,
  type TemplateFieldDatasourceItem,
} from 'shared/api/templateService'
import type { PermitWorkflowTimeline } from 'modules/permit/api/permitService'
import { GENERAL_FIELD_STATE_MAP } from 'modules/permit/utils/permitFieldMapping'
import {
  AdditionalDependencyRule,
  ParsedFieldConfig,
  resolveRuleValue,
  sanitizeFieldString,
  tryParseJson,
} from './permit-request/utils/templateFieldUtils'
import { GeneralDetailsSection } from './permit-request/sections/GeneralDetailsSection'
import { PermitAdditionalSections } from './permit-request/sections/PermitAdditionalSections'
import { PermitWorkflowSectionsPanel } from './permit-request/sections/PermitWorkflowSectionsPanel'
import { useWorkflowSections } from 'modules/permit/pages/permit-request/hooks/useWorkflowSections'
import { useDatasourceDependencies, type FieldDependencyInfo } from 'modules/permit/pages/permit-request/hooks/useDatasourceDependencies'
import { useTemplateFieldState } from 'modules/permit/pages/permit-request/hooks/useTemplateFieldState'
import { usePermitDateTime, parseTimeParts } from 'modules/permit/pages/permit-request/hooks/usePermitDateTime'
import { useTemplateFieldRenderer } from 'modules/permit/pages/permit-request/hooks/useTemplateFieldRenderer'
import { useWorkflowStageMetadata } from 'modules/permit/pages/permit-request/hooks/useWorkflowStageMetadata'
import { usePermitGeneralDetails } from 'modules/permit/pages/permit-request/hooks/usePermitGeneralDetails'
import { collectNormalizedUserRoleCodeCandidates } from 'modules/permit/pages/permit-request/utils/pageHelpers'
import type { PermitFormState } from 'modules/permit/pages/permit-request/types'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'shared/ui/dialog'
import { Button } from 'shared/ui/button'
import { Loader2 } from 'lucide-react'
import { cn } from 'shared/ui/utils'

const normalizeScalarValue = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined
  }
  if (typeof value === 'string') {
    return value.trim().length > 0 ? value.trim() : undefined
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return undefined
}

const resolveFallbackDatasourceValue = (
  item?: TemplateFieldDatasourceItem | Record<string, unknown>,
): string | undefined => {
  if (!item) {
    return undefined
  }

  const directValue = normalizeScalarValue((item as TemplateFieldDatasourceItem).value)
  if (directValue) {
    return directValue
  }
  const directLabel = normalizeScalarValue((item as TemplateFieldDatasourceItem).label)
  if (directLabel) {
    return directLabel
  }

  const record = item as Record<string, unknown>
  const fallbackKeys = ['result', 'text', 'name', 'code']
  for (const key of fallbackKeys) {
    const candidate = normalizeScalarValue(record[key])
    if (candidate) {
      return candidate
    }
  }

  for (const [key, raw] of Object.entries(record)) {
    if (key === 'columns') {
      continue
    }
    const candidate = normalizeScalarValue(raw)
    if (candidate) {
      return candidate
    }
  }

  const columnsRaw = record.columns as unknown
  if (columnsRaw && typeof columnsRaw === 'object') {
    for (const raw of Object.values(columnsRaw as Record<string, unknown>)) {
      const candidate = normalizeScalarValue(raw)
      if (candidate) {
        return candidate
      }
    }
  }

  return undefined
}

interface RequiredDocumentItem {
  id: string
  name: string
  code: string
  isMandatory: boolean
}

interface PermitRequestFormProps {
  permitData: PermitFormState
  setPermitData: Dispatch<SetStateAction<PermitFormState>>
  setGeneralFieldLabels: Dispatch<SetStateAction<Record<string, string>>>
  generalFieldLabels?: Record<string, string>
  selectedWorkers: WorkerSelectionItem[]
  setSelectedWorkers: (workers: WorkerSelectionItem[]) => void
  selectedActivities: string[]
  setSelectedActivities: (activities: string[]) => void
  uploadedFiles: UploadedFileItem[]
  setUploadedFiles: Dispatch<SetStateAction<UploadedFileItem[]>>
  extraFieldValues: Record<string, string>
  setExtraFieldValues: Dispatch<SetStateAction<Record<string, string>>>
  riskData: any
  setRiskData: Dispatch<SetStateAction<any>>
  workAreaSelection: WorkAreaSelection
  setWorkAreaSelection: Dispatch<SetStateAction<WorkAreaSelection>>
  requiredDocuments?: RequiredDocumentItem[]
  isDocumentsLoading?: boolean
  documentsError?: string | null
  currentUser?: any
  permitStatus?: string
  permitTypes: PermitType[]
  isPermitTypeLoading?: boolean
  permitTypeError?: string | null
  templateConfig: TemplateConfiguration | null
  isTemplateLoading?: boolean
  templateError?: string | null
  tenantId: string
  onPermitTypeChange?: (permit: PermitType | null) => void
  isEditMode?: boolean
  isReadOnly?: boolean
  activeApprovalStageId?: string | null
  canApproveCurrentStage?: boolean
  workflowStageSequences?: Record<string, number> | null
  currentStageSequence?: number | null
  activeStageCode?: string | null
  workflowTimeline?: PermitWorkflowTimeline | null
  submitValidationTargetKeys?: string[]
  submitValidationAttemptToken?: number
  approvalFormDialog?: {
    open: boolean
    onCancel: () => void
    onConfirm: () => void
    isProcessing?: boolean
    missingFields?: string[]
    stageName?: string
    validationError?: string | null
    actionError?: string | null
  }
}

/**
 * Renders the full permit request form driven by the selected template.
 * Handles mapping template metadata to the appropriate UI controls while delegating
 * specialised interactions (workers dialog, sub-activities, etc.) to child components.
 */
export function PermitRequestForm({
  permitData,
  setPermitData,
  setGeneralFieldLabels,
  generalFieldLabels = {},
  selectedWorkers,
  setSelectedWorkers,
  selectedActivities,
  setSelectedActivities,
  uploadedFiles,
  setUploadedFiles,
  extraFieldValues,
  setExtraFieldValues,
  riskData,
  setRiskData,
  workAreaSelection,
  setWorkAreaSelection,
  requiredDocuments = [],
  isDocumentsLoading = false,
  documentsError = null,
  currentUser,
  permitStatus,
  permitTypes,
  isPermitTypeLoading = false,
  permitTypeError = null,
  templateConfig,
  isTemplateLoading = false,
  templateError = null,
  tenantId,
  onPermitTypeChange,
  isEditMode = false,
  isReadOnly = false,
  activeApprovalStageId = null,
  canApproveCurrentStage = false,
  workflowStageSequences = null,
  currentStageSequence = null,
  activeStageCode = null,
  workflowTimeline = null,
  submitValidationTargetKeys = [],
  submitValidationAttemptToken = 0,
  approvalFormDialog,
}: PermitRequestFormProps) {
  const isValidationKeyInvalid = useCallback(
    (key?: string | null) => typeof key === 'string' && submitValidationTargetKeys.includes(key),
    [submitValidationTargetKeys],
  )

  const getValidationContainerClass = useCallback(
    (key?: string | null) =>
      cn(
        'rounded-lg transition-colors',
        isValidationKeyInvalid(key) ? 'bg-rose-50/70 ring-2 ring-rose-300 ring-offset-2 ring-offset-white' : '',
      ),
    [isValidationKeyInvalid],
  )

  useEffect(() => {
    if (submitValidationAttemptToken === 0 || submitValidationTargetKeys.length === 0) {
      return
    }

    const firstInvalidElement = submitValidationTargetKeys
      .map((key) => document.querySelector<HTMLElement>(`[data-validation-key="${key}"]`))
      .find((element): element is HTMLElement => Boolean(element))

    if (!firstInvalidElement) {
      return
    }

    firstInvalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const focusTarget = firstInvalidElement.querySelector<HTMLElement>(
      'input:not([disabled]), textarea:not([disabled]), button:not([disabled]), [role="combobox"], [tabindex]:not([tabindex="-1"])',
    )
    focusTarget?.focus({ preventScroll: true })
  }, [submitValidationAttemptToken, submitValidationTargetKeys])

  const {
    dateErrors,
    resetDateErrors,
    validateDates,
    handleStartDateChange,
    handleEndDateChange,
    handleTimeHourChange,
    handleTimeMinuteChange,
    timeHourOptions,
    timeMinuteOptions,
  } = usePermitDateTime({
    permitData,
    setPermitData,
  })

  const permitDataState = permitData as Record<string, any>

  const handleGeneralFieldLabelChange = useCallback(
    (fieldCode: string, label: string) => {
      const normalizedLabel = normalizeScalarValue(label) ?? ''
      setGeneralFieldLabels((prev) => {
        if (!normalizedLabel) {
          if (!(fieldCode in prev)) {
            return prev
          }
          const next = { ...prev }
          delete next[fieldCode]
          return next
        }
        if (prev[fieldCode] === normalizedLabel) {
          return prev
        }
        return {
          ...prev,
          [fieldCode]: normalizedLabel,
        }
      })
    },
    [setGeneralFieldLabels],
  )

  const {
    workflowSections,
    generalFields,
    generalFieldMap,
    getStoredTemplateFieldValue,
    getStoredTemplateFieldLabel,
    setTemplateFieldValue,
    setTemplateFieldLabel,
  } = useTemplateFieldState({
    templateConfig,
    extraFieldValues,
    setExtraFieldValues,
  })

  const setTemplateFieldValueWithGeneralSync = useCallback(
    (field: TemplateField, value: string | boolean | null | undefined) => {
      setTemplateFieldValue(field, value)

      const code = field.code?.trim().toUpperCase()
      if (!code || code === 'CONTROLLER') {
        return
      }

      const stateKey = GENERAL_FIELD_STATE_MAP[code]
      if (!stateKey) {
        return
      }

      setPermitData((prev: PermitFormState) => {
        let nextValue: any = value ?? ''
        const fieldType = (field.fieldType ?? '').trim().toLowerCase()

        if (fieldType === 'checkbox' || fieldType === 'boolean') {
          nextValue = value === true
        } else if (typeof nextValue !== 'string') {
          nextValue = nextValue == null ? '' : String(nextValue)
        }

        if ((prev as any)[stateKey] === nextValue) {
          return prev
        }

        return {
          ...prev,
          [stateKey]: nextValue,
        }
      })
    },
    [setPermitData, setTemplateFieldValue],
  )

  const {
    normalizedActiveStageId,
    normalizedActiveStageCode,
    stageSequenceMap,
    templateStageOrderMap,
    activeStageTemplateIndex,
    activeStageSequence,
    doesSectionMatchActiveStage,
  } = useWorkflowStageMetadata({
    workflowSections,
    workflowStageSequences,
    activeApprovalStageId,
    activeStageCode,
    currentStageSequence,
  })
  const currentUserRoleCodeCandidates = useMemo(
    () => collectNormalizedUserRoleCodeCandidates(currentUser),
    [currentUser],
  )
  const parseFieldConfigMemo = useCallback((field: TemplateField): ParsedFieldConfig | null => {
    if (!field.config) return null
    const trimmed = field.config.trim()
    if (!trimmed) return null
    try {
      return JSON.parse(trimmed) as ParsedFieldConfig
    } catch {
      return null
    }
  }, [])

  const resolveFieldValueByCode = useCallback(
    (fieldCode?: string): string | undefined => {
      if (!fieldCode) {
        return undefined
      }

      const upperCased = fieldCode.toUpperCase()
      const lowerCased = fieldCode.toLowerCase()
      const stateKey = GENERAL_FIELD_STATE_MAP[upperCased]
      if (stateKey) {
        if (upperCased === 'STTIME' || upperCased === 'ENTIME') {
          const baseValue = sanitizeFieldString(permitDataState?.[stateKey])
          if (!baseValue) {
            const directMatch =
              extraFieldValues[fieldCode] ?? extraFieldValues[upperCased] ?? extraFieldValues[lowerCased]
            return typeof directMatch === 'string' ? sanitizeFieldString(directMatch) : undefined
          }
          return baseValue
        }

        const rawValue = permitDataState?.[stateKey]
        if (rawValue === null || rawValue === undefined) {
          return undefined
        }

        if (typeof rawValue === 'string') {
          return sanitizeFieldString(rawValue)
        }

        if (typeof rawValue === 'object') {
          if ('value' in rawValue && typeof rawValue.value === 'string') {
            const value = sanitizeFieldString(rawValue.value)
            if (value) {
              return value
            }
          }
          if ('label' in rawValue && typeof rawValue.label === 'string') {
            const label = sanitizeFieldString(rawValue.label)
            if (label) {
              return label
            }
          }
        }

        const directMatch =
          extraFieldValues[fieldCode] ?? extraFieldValues[upperCased] ?? extraFieldValues[lowerCased]
        if (typeof directMatch === 'string') {
          return sanitizeFieldString(directMatch)
        }

        return undefined
      }

      const directMatch =
        extraFieldValues[fieldCode] ?? extraFieldValues[upperCased] ?? extraFieldValues[lowerCased]

      if (typeof directMatch === 'string') {
        return sanitizeFieldString(directMatch)
      }

      return undefined
    },
    [extraFieldValues, permitDataState],
  )

  const getStoredTemplateFieldValueWithGeneral = useCallback(
    (field: TemplateField): string | undefined => {
      const code = field.code?.trim()
      if (code) {
        const upperCode = code.toUpperCase()
        if (GENERAL_FIELD_STATE_MAP[upperCode]) {
          return resolveFieldValueByCode(code)
        }
      }

      return getStoredTemplateFieldValue(field)
    },
    [getStoredTemplateFieldValue, resolveFieldValueByCode],
  )

  const getTemplateFieldDisplayValueWithGeneral = useCallback(
    (field: TemplateField) => getStoredTemplateFieldValueWithGeneral(field) ?? '',
    [getStoredTemplateFieldValueWithGeneral],
  )

  const getFieldDependencyInfo = useCallback(
    (field: TemplateField): FieldDependencyInfo => {
      const params: Record<string, string> = {}
      let dependencyMissing = false

      const config = parseFieldConfigMemo(field)
      const dependencyRules: AdditionalDependencyRule[] = []

      if (config?.dependsOn) {
        dependencyRules.push({
          fieldCode: config.dependsOn,
          parameterName: config.dependencyConfig?.parameterName,
          requiredForLoad: config.dependencyConfig?.requiredForLoad,
          timeFieldCode:
            config.dependencyConfig?.timeFieldCode || config.dependencyConfig?.combineWithFieldCode,
          combineWithFieldCode: config.dependencyConfig?.combineWithFieldCode,
          valueFormat: config.dependencyConfig?.valueFormat,
        })
      }

      if (config && Array.isArray(config.extraDependencies)) {
        config.extraDependencies.forEach((rule) => {
          if (!rule || typeof rule.fieldCode !== 'string' || rule.fieldCode.trim().length === 0) {
            return
          }
          dependencyRules.push({
            fieldCode: rule.fieldCode,
            parameterName: rule.parameterName,
            requiredForLoad: rule.requiredForLoad,
            timeFieldCode: rule.timeFieldCode || rule.combineWithFieldCode,
            combineWithFieldCode: rule.combineWithFieldCode,
            valueFormat: rule.valueFormat,
          })
        })
      }

      dependencyRules.forEach((rule) => {
        const resolvedValue = resolveRuleValue(rule, resolveFieldValueByCode)
        if (!resolvedValue) {
          if (rule.requiredForLoad) {
            dependencyMissing = true
          }
          return
        }

        const parameterName = rule.parameterName?.trim() || rule.fieldCode.toLowerCase()
        params[parameterName] = resolvedValue
      })

      const dependencyValue =
        Object.keys(params).length > 0 && !dependencyMissing ? JSON.stringify(params) : undefined

      return { params, dependencyValue, dependencyMissing, config }
    },
    [parseFieldConfigMemo, resolveFieldValueByCode],
  )

  const resolveDatasourceStaticParams = useCallback(
    (field: TemplateField): Record<string, string> => {
      const raw = field.datasource?.paramsJson
      if (!raw) {
        return {}
      }

      const parsed = tryParseJson<Record<string, unknown>>(raw)
      if (!parsed || typeof parsed !== 'object') {
        return {}
      }

      const replaceTokens = (value: string) =>
        value
          .replace(/@tenantId/gi, tenantId)
          .replace(/@tenant_id/gi, tenantId)
          .replace(/@tenant/gi, tenantId)

      return Object.entries(parsed).reduce<Record<string, string>>((acc, [key, value]) => {
        if (typeof value !== 'string') {
          return acc
        }
        const trimmed = value.trim()
        if (!trimmed) {
          return acc
        }
        acc[key] = replaceTokens(trimmed)
        return acc
      }, {})
    },
    [tenantId],
  )

  const {
    workLocationFieldId,
    unitField,
    unitFieldId,
    controllerField,
    controllerDependencyInfo,
    controllerDependencyLabel,
  } = usePermitGeneralDetails({
    generalFieldMap,
    getFieldDependencyInfo,
  })

  const {
    visibleWorkflowSections,
    isStageEditor,
    isReadOnlyViewer,
  } = useWorkflowSections({
    workflowSections,
    stageSequenceMap,
    templateStageOrderMap,
    activeStageSequence,
    activeStageTemplateIndex,
    normalizedActiveStageId,
    normalizedActiveStageCode,
    isReadOnly,
    canApproveCurrentStage,
    currentUserRoleCodeCandidates,
    permitStatus,
  })

  const {
    getFieldOptions,
    isFieldLoading,
    dependencyCacheRef,
    searchSelectLabels,
    setSearchSelectLabels,
    setFieldOptions,
  } = useDatasourceDependencies({
    tenantId,
    templateVersionId: templateConfig?.templateVersionId ?? null,
    generalFields,
    workflowSections,
    visibleWorkflowSections,
    isEditMode,
    isReadOnly,
    isStageEditor,
    isReadOnlyViewer,
    canApproveCurrentStage,
    doesSectionMatchActiveStage,
    parseFieldConfig: parseFieldConfigMemo,
    getFieldDependencyInfo,
    resolveDatasourceStaticParams,
    resolveFallbackDatasourceValue,
    setTemplateFieldValue: setTemplateFieldValueWithGeneralSync,
    setTemplateFieldLabel,
    getStoredTemplateFieldValue: getStoredTemplateFieldValueWithGeneral,
    getStoredTemplateFieldLabel,
  })

  const previousTemplateVersion = useRef<string | null>(null)

  useEffect(() => {
    const currentVersion = templateConfig?.templateVersionId ?? null

    if (currentVersion !== previousTemplateVersion.current) {
      previousTemplateVersion.current = currentVersion
      setPermitData((prev: any) => ({
        ...prev,
        workDescription: '',
        workLocation: '',
        unit: '',
        building: '',
        floor: '',
        room: '',
        other: '',
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        controller: null,
      }))
      resetDateErrors()
      setExtraFieldValues({})
    }
  }, [resetDateErrors, setExtraFieldValues, setPermitData, templateConfig])

  const renderTemplateFieldInput = useTemplateFieldRenderer({
    getFieldOptions,
    getTemplateFieldDisplayValue: getTemplateFieldDisplayValueWithGeneral,
    getStoredTemplateFieldLabel,
    getFieldValueByCode: resolveFieldValueByCode,
    isFieldLoading,
    setTemplateFieldValue: setTemplateFieldValueWithGeneralSync,
    tenantId,
    permitNumber: permitData.permitNo,
    parseFieldConfig: parseFieldConfigMemo,
    searchSelectLabels,
    setSearchSelectLabels,
    setTemplateFieldLabel,
    isReadOnly,
    canApproveCurrentStage,
    getFieldDependencyInfo,
  })
  const stageApprovalSections = useMemo(
    () => workflowSections.filter((section) => doesSectionMatchActiveStage(section)),
    [workflowSections, doesSectionMatchActiveStage],
  )
  const resolveColumnSpanClass = useCallback((field?: TemplateField | null): string => {
    const layout = typeof field?.layout === 'number' ? field.layout : null
    return layout === 1 ? 'md:col-span-2' : 'md:col-span-1'
  }, [])
  const clearTimeField = useCallback(
    (fieldKey: 'startTime' | 'endTime') => {
      setPermitData((prev: any) => ({ ...prev, [fieldKey]: '' }))
    },
    [setPermitData],
  )

  const startTimeParts = useMemo(() => parseTimeParts(permitData.startTime), [permitData.startTime])
  const endTimeParts = useMemo(() => parseTimeParts(permitData.endTime), [permitData.endTime])

  // Populate user data (do not generate permit number on client; server will generate on first save)
  useEffect(() => {
    if (
      currentUser &&
      (!permitData.requestorName || !permitData.requestorCompany)
    ) {
      setPermitData((prev: any) => ({
        ...prev,
        requestorName: prev.requestorName || currentUser?.name || "",
        requestorCompany: prev.requestorCompany || currentUser?.company || "",
      }))
    }
  }, [currentUser, permitData.requestorName, permitData.requestorCompany, setPermitData])

  // Validate dates when component mounts or data changes
  useEffect(() => {
    if (permitData.startDate || permitData.endDate) {
      validateDates(permitData.startDate, permitData.endDate)
    }
  }, [permitData.startDate, permitData.endDate, validateDates])

  const hasSelectedPermitType = useMemo(
    () => Boolean(templateConfig?.templateVersionId || permitData.permitTypeCode || permitData.permitType),
    [permitData.permitType, permitData.permitTypeCode, templateConfig?.templateVersionId],
  )
  const isGeneralEnabled = templateConfig?.generalEnabled !== false
  const isSubactivityEnabled = templateConfig?.subactivityEnabled !== false
  const isWorkerEnabled = templateConfig?.workerEnabled !== false
  const isMapEnabled = templateConfig?.mapEnabled !== false
  const isRamEnabled = templateConfig?.ramEnabled !== false
  const isAttachmentEnabled = templateConfig?.attachmentEnabled !== false

  return (
    <div className="space-y-6">
      <GeneralDetailsSection
        isReadOnly={isReadOnly}
        isEditMode={isEditMode}
        permitData={permitData}
        setPermitData={setPermitData}
        currentUser={currentUser}
        permitTypes={permitTypes}
        isPermitTypeLoading={isPermitTypeLoading}
        permitTypeError={permitTypeError}
        onPermitTypeChange={onPermitTypeChange}
        templateError={templateError}
        isTemplateLoading={isTemplateLoading}
        isGeneralEnabled={isGeneralEnabled}
        generalFields={generalFields}
        generalFieldMap={generalFieldMap}
        renderTemplateFieldInput={renderTemplateFieldInput}
        unitField={unitField}
        workLocationFieldId={workLocationFieldId}
        unitFieldId={unitFieldId}
        dependencyCacheRef={dependencyCacheRef}
        setFieldOptions={setFieldOptions}
        getFieldOptions={getFieldOptions}
        isFieldLoading={isFieldLoading}
        handleStartDateChange={handleStartDateChange}
        handleEndDateChange={handleEndDateChange}
        dateErrors={dateErrors}
        startTimeParts={startTimeParts}
        endTimeParts={endTimeParts}
        handleTimeHourChange={handleTimeHourChange}
        handleTimeMinuteChange={handleTimeMinuteChange}
        clearTimeField={clearTimeField}
        timeHourOptions={timeHourOptions}
        timeMinuteOptions={timeMinuteOptions}
        controllerField={controllerField}
        controllerDependencyInfo={controllerDependencyInfo}
        controllerDependencyLabel={controllerDependencyLabel}
        tenantId={tenantId}
        canApproveCurrentStage={Boolean(canApproveCurrentStage)}
        onGeneralFieldLabelChange={handleGeneralFieldLabelChange}
        generalFieldLabels={generalFieldLabels}
        getValidationContainerClass={getValidationContainerClass}
      />
      {hasSelectedPermitType ? (
        <>
          <PermitWorkflowSectionsPanel
            sections={visibleWorkflowSections}
            isReadOnly={isReadOnly}
            canApproveCurrentStage={Boolean(canApproveCurrentStage)}
            doesSectionMatchActiveStage={doesSectionMatchActiveStage}
            currentUserRoleCodeCandidates={currentUserRoleCodeCandidates}
            permitStatus={permitStatus}
            renderTemplateFieldInput={renderTemplateFieldInput}
            workflowTimeline={workflowTimeline}
            getValidationContainerClass={getValidationContainerClass}
          />
          <PermitAdditionalSections
            tenantId={tenantId}
            isReadOnly={isReadOnly}
            canApproveCurrentStage={Boolean(canApproveCurrentStage)}
            permitNo={permitData.permitNo}
            isSubactivityEnabled={isSubactivityEnabled}
            isWorkerEnabled={isWorkerEnabled}
            isMapEnabled={isMapEnabled}
            isRamEnabled={isRamEnabled}
            isAttachmentEnabled={isAttachmentEnabled}
            selectedWorkers={selectedWorkers}
            setSelectedWorkers={setSelectedWorkers}
            subActivities={templateConfig?.subActivities ?? []}
            selectedActivities={selectedActivities}
            setSelectedActivities={setSelectedActivities}
          extraFieldValues={extraFieldValues}
          setExtraFieldValues={setExtraFieldValues}
          workAreaSelection={workAreaSelection}
          setWorkAreaSelection={setWorkAreaSelection}
          workLocationLabel={generalFieldLabels[GENERAL_FIELD_STATE_MAP.WORKLOCATION] ?? permitData.workLocation}
          permitData={{ workLocation: permitData.workLocation, unit: permitData.unit }}
          riskData={riskData}
          setRiskData={setRiskData}
            requiredDocuments={requiredDocuments}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            isDocumentsLoading={isDocumentsLoading}
            documentsError={documentsError}
            getValidationContainerClass={getValidationContainerClass}
          />
        </>
      ) : null}
      {approvalFormDialog ? (
        <Dialog
          open={approvalFormDialog.open}
          onOpenChange={(open) => {
            if (!open) {
              approvalFormDialog.onCancel()
            }
          }}
        >
          <DialogContent className="max-w-5xl">
            <DialogHeader>
              <DialogTitle>กรอกข้อมูลก่อนอนุมัติ</DialogTitle>
              <p className="text-sm text-slate-500">
                {approvalFormDialog.stageName
                  ? `ขั้นตอน: ${approvalFormDialog.stageName}`
                  : 'กรุณากรอกข้อมูลที่ระบบกำหนดก่อนกดยืนยันอนุมัติ'}
              </p>
            </DialogHeader>
            {approvalFormDialog.missingFields && approvalFormDialog.missingFields.length > 0 ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                <p className="font-medium">จำเป็นต้องกรอก:</p>
                <ul className="mt-1 list-disc space-y-1 pl-5">
                  {approvalFormDialog.missingFields.map((label) => (
                    <li key={label}>{label}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {approvalFormDialog.validationError ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {approvalFormDialog.validationError}
              </div>
            ) : null}
            {approvalFormDialog.actionError ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {approvalFormDialog.actionError}
              </div>
            ) : null}
            <div className="max-h-[55vh] space-y-4 overflow-y-auto">
              {stageApprovalSections.length > 0 ? (
                stageApprovalSections.map((section) => (
                  <fieldset
                    key={section.templateSectionId ?? section.code}
                    disabled={!canApproveCurrentStage}
                    className="space-y-4 rounded-md border border-slate-200 bg-white/50 p-4"
                  >
                    <div className="text-sm font-semibold text-slate-900">
                      {section.title || section.code || 'Workflow Section'}
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      {section.fields?.map((field) => {
                        const node = renderTemplateFieldInput(field, {
                          disabled: !canApproveCurrentStage,
                        })
                        if (!node) {
                          return null
                        }
                        return (
                          <div
                            key={field.fieldId ?? field.code}
                            className={resolveColumnSpanClass(field)}
                          >
                            {node}
                          </div>
                        )
                      })}
                    </div>
                  </fieldset>
                ))
              ) : (
                <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                  ไม่พบฟิลด์สำหรับขั้นตอนนี้
                </div>
              )}
            </div>
            <DialogFooter className="justify-end gap-2">
              <Button variant="outline" onClick={approvalFormDialog.onCancel} disabled={approvalFormDialog.isProcessing}>
                ยกเลิก
              </Button>
              <Button
                onClick={approvalFormDialog.onConfirm}
                disabled={approvalFormDialog.isProcessing || !canApproveCurrentStage}
              >
                {approvalFormDialog.isProcessing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    กำลังอนุมัติ...
                  </>
                ) : (
                  'ยืนยันอนุมัติ'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  )
}
